<script setup lang="ts">
import type { Ref } from "vue";
import { UseFocusTrap } from "@vueuse/integrations/useFocusTrap/component";

export interface BaseModalProps {
  title?: string | Ref<string>;
  description?: string;
  rootClass?: string;
  noBackDropClick?: boolean;
}

const props = defineProps<BaseModalProps>();
const emit = defineEmits(["open", "close"]);

const isModalOpen = ref(false);
const vFocus = {
  mounted: (el: HTMLElement) => el.focus(),
};

const openModal = () => {
  emit("open");
  isModalOpen.value = true;
};

const closeModal = () => {
  if (props.noBackDropClick)
    return;
  emit("close");
  isModalOpen.value = false;
};

onMounted(() => {
  const escapeHandler = (e: KeyboardEvent) => {
    if (e.keyCode === 27 && isModalOpen.value)
      closeModal();
  };
  document.addEventListener("keydown", escapeHandler);
});
</script>

<template>
  <slot name="trigger" :open-modal="openModal">
    <h1>Modal Example</h1>
    <button class="button" @click="openModal()">
      Open Modal
    </button>
  </slot>

  <Teleport to="body">
    <UseFocusTrap v-if="isModalOpen">
      <div class="baseModal">
        <div class="baseModal__mask">
          <div class="baseModal__wrapper" @click="closeModal()">
            <div
              v-focus
              :class="props.rootClass"
              class="baseModal__container"
              role="dialog"
              aria-labelledby="modalHeading"
              aria-modal="true"
              tabindex="-1"
              @click.stop
            >
              <div ref="firstModalElRef" />

              <BaseButton type="icon" :disabled="props.noBackDropClick" name="modalClose" title="Close" class="baseModal__close" @click="closeModal()">
                <IconClose fill="var(--base-icon-color)" />
              </BaseButton>

              <slot name="before-title" />

              <h2 id="modalHeading" class="text-main text-heading-2 text-center">
                {{ props.title }}
              </h2>

              <p v-if="props.description" class="mt-4 text-body-medium text-secondary text-center">
                {{ props.description }}
              </p>

              <slot name="actions" :close-modal="closeModal" />
            </div>
          </div>
        </div>
      </div>
    </UseFocusTrap>
  </Teleport>
</template>

<style lang="scss">
// .baseModal {
.baseModal__mask {
  @apply table fixed z-[9998] top-0 left-0 w-full h-full
  bg-black bg-opacity-60 transition-opacity duration-300;
}
.baseModal__wrapper {
  @apply table-cell align-middle;
}

.baseModal__container {
  @apply relative rounded mx-auto my-0 overflow-auto py-10 px-[1.25rem] md:px-[3.25rem];

  background-color: var(--base-card-bgColor);
}

.baseModal__close {
  @apply absolute top-4 right-4;
}
// }
</style>
